import React from "react";
import FormField from "./FormField";
import { useAppSelector } from "../../store/hooks";

interface LabPreferenceSectionProps {
  labPreferences: any;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    section: string,
  ) => void;
  variant?: "cases" | "smile" | "uris";
}

const LabPreferenceSection: React.FC<LabPreferenceSectionProps> = ({
  labPreferences,
  handleChange,
  variant = "cases",
}) => {
  const {user} = useAppSelector((state)=>state.auth)
  const isSmile = variant === "smile";
  const isUris = variant === "uris";

  const sectionHeadingClass = isUris
    ? "text-[15px] mt-4 font-bold text-white border-b-2 border-[#c31c30] uppercase mb-4"
    : isSmile
    ? "text-[15px] mt-4 font-bold text-white border-b-2 border-cyan-500 uppercase mb-4"
    : "text-[15px] mt-4 font-bold text-gray-800 border-b-2 border-[#2fa9bf] uppercase mb-4";

  const subHeadingClass = isUris
    ? "border-b border-[#2e3036] mb-2 text-md text-[#c5c7cc] font-medium"
    : isSmile
    ? "border-b-2 border-[#1f2221] mb-2 text-md text-[#c7ccca] font-medium"
    : "border-b-2 mb-2 text-md text-gray-800 font-medium";

  return (
    <>
      <h2 className={sectionHeadingClass}>
        LAB PREFERENCES
      </h2>

      {/* Parameters */}
      <div>
        <p className={subHeadingClass}>
          Parameters
        </p>
        {[
          { label: "Occlusal Clearance", name: "occlusal_clearance" },
          { label: "Proximal Contact", name: "proximal_contact" },
          { label: "Cement Gap", name: "cement_gap" },
          { label: "Extra Cement Gap", name: "extra_cement_gap" },
          { label: "Drill Radius", name: "drill_radius" },
          { label: "Magin Parameter", name: "margin_parameter" },
        ].map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            value={labPreferences.parameters[field.name]}
            onChange={(e) => handleChange(e, "parameters")}
            variant={variant}
          />
        ))}

        <FormField
          label="Pontic design"
          name="pontic_design"
          value={labPreferences.parameters.pontic_design}
          onChange={(e) => handleChange(e, "parameters")}
          variant={variant}
          options={[
            "modified-ridge-lap",
            "saddle-ridge-lap",
            "hygienic",
            "conical",
            "ovate",
          ]}
        />
      </div>

      {/* Anatomy */}
      <div>
        <p className={subHeadingClass}>
          Anatomy Preference
        </p>
        <FormField
          label="Library"
          name="library"
          value={labPreferences.anatomy.library}
          onChange={(e) => handleChange(e, "anatomy")}
          variant={variant}
        />
        <FormField
          label="Preference"
          name="anatomy_preference"
          value={labPreferences.anatomy.anatomy_preference}
          onChange={(e) => handleChange(e, "anatomy")}
          variant={variant}
          options={["anatomic", "semi-anatomic"]}
        />
      </div>

      {/* Library Preference */}
      <div>
        <p className={subHeadingClass}>
          Library Preference
        </p>
        {[
          { label: "Any preference", name: "any_preference" },
          { label: "Best Match", name: "best_match" },
        ].map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            value={labPreferences.library_preference[field.name]}
            onChange={(e) => handleChange(e, "library_preference")}
            variant={variant}
          />
        ))}
      </div>

      {/* Software */}
      <div>
        <p className={subHeadingClass}>
          Software
        </p>
        {[
          { label: "Scanner Used", name: "scanner_used" },
          { label: "Design Software Used/Preferred", name: "design_software" },
          { label: "Current Software Version", name: "software_version" },
        ].map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            value={labPreferences.software[field.name]}
            onChange={(e) => handleChange(e, "software")}
            variant={variant}
          />
        ))}
      </div>

      {/* Model Preference */}
      <div>
        <p className={subHeadingClass}>
          Model Preference
        </p>
        {[{ label: "Type", name: "type" }].map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            value={labPreferences.modelPreference[field.name]}
            onChange={(e) => handleChange(e, "modelPreference")}
            variant={variant}
          />
        ))}
      </div>

      {/* Files Preference */}
      {(user?.role === "User" || user?.role === "Side Admin")  ? null : <div>
        <p className={subHeadingClass}>
          Files Preference
        </p>
        {[
          { label: "Files", name: "files" }
        ].map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            value={labPreferences.files_preference[field.name]}
            onChange={(e) => handleChange(e, "files_preference")}
            variant={variant}
          />
        ))}
      </div>}
      

      {/* Other Preference */}
      <div>
        <p className={subHeadingClass}>
          Other Preferences
        </p>
        {[
          { label: "Natural Anatomy", name: "natural_anatomy" },
          { label: "Ovate Pontic", name: "ovate_pontic" },
          { label: "Read Notes Carefully", name: "read_notes_carefully" },
        ].map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            value={labPreferences.otherPreference[field.name]}
            onChange={(e) => handleChange(e, "otherPreference")}
            variant={variant}
          />
        ))}
      </div>
    </>
  );
};

export default LabPreferenceSection;
